import DOMPurify from 'dompurify';

/**
 * Mail / Notice / 일반 HTML 본문 client-side sanitize (XSS 방어, defense in depth).
 *
 * cross-batch review Stage 1 HIGH-7 봉합 (Mail compose POST raw HTML 저장 stored XSS).
 * backend 의 sanitize-html 과 양쪽 적용 — 한쪽 우회 시에도 차단.
 *
 * 정책:
 *   - script / iframe / object / embed / form 모두 제거.
 *   - on-event (onclick / onerror 등) 모두 제거.
 *   - href / src 의 javascript: / data: / vbscript: 스킴 차단.
 *   - a.target=_blank → rel=noopener noreferrer 강제.
 */

const ALLOWED_TAGS = [
  'p', 'br', 'span', 'div',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'strong', 'em', 'b', 'i', 'u', 's', 'strike', 'sub', 'sup',
  'a',
  'ul', 'ol', 'li',
  'blockquote', 'pre', 'code',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'hr',
  'img',
];

const ALLOWED_ATTR = [
  'href', 'name', 'target', 'rel', 'title', 'src', 'alt', 'width', 'height',
  'border', 'cellspacing', 'cellpadding', 'colspan', 'rowspan', 'align',
  'class', 'id',
];

/**
 * 본문 이미지 빼기 (2026-09-05) — 「환경설정 > 기본환경 > 본문 이미지 처리」.
 *
 * ★ 허용 목록에서 `img` 만 뺀다. **막는 목록에 넣지 않는다** — FORBID 는 태그를 통째로
 *   지우지만, 허용 목록에서 빼면 DOMPurify 가 태그만 벗기고 **안의 글자는 남긴다**.
 *   여기서는 결과가 같지만(img 는 빈 태그), 규칙을 「허용 목록 한 곳」에 모아 둬야
 *   다음에 다른 태그를 이 스위치에 얹을 때 두 곳을 안 고친다.
 * ★ 옵션이 없으면 **지금까지와 똑같다** — 부르는 쪽을 한 곳도 안 고쳐도 된다.
 */
export interface SanitizeMailOptions {
  /** 본문의 이미지를 아예 그리지 않는다. 기본은 false(지금까지의 동작). */
  blockImages?: boolean;
}

function tagsFor(base: readonly string[], opts?: SanitizeMailOptions): string[] {
  return opts?.blockImages ? base.filter((t) => t !== 'img') : [...base];
}

export function sanitizeMailHtml(rawHtml: string, opts?: SanitizeMailOptions): string {
  if (typeof rawHtml !== 'string' || rawHtml.length === 0) return '';
  const clean = DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: tagsFor(ALLOWED_TAGS, opts),
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
    ADD_ATTR: ['target', 'rel'],
  });
  // a.target=_blank → rel=noopener noreferrer 보강. DOMPurify hook 또는 후처리.
  return clean.replace(/<a([^>]*?)target="_blank"((?:(?!rel=).)*?)>/gi, (match, before, after) => {
    if (/rel=/i.test(match)) return match;
    return `<a${before}target="_blank" rel="noopener noreferrer"${after}>`;
  });
}

/**
 * 가져온 외부 메일 본문 sanitize (2026-08-20, migration 112).
 *
 * `sanitizeMailHtml` 은 **우리가 쓴 본문** 정책이라 `style` 을 지운다. 바깥에서 온 메일은
 * 표와 인라인 style 로 짜여 있어서, 그 정책을 그대로 대면 서식이 통째로 무너진다
 * (서버 쪽 `sanitizeImportedMailBody` 와 짝이다 — 서버가 남긴 것을 화면이 다시 지우면
 * 두 번 손해다).
 *
 * 위험한 것은 그대로 막는다: script/iframe/object/form/style 태그 · on* 이벤트 ·
 * javascript:/data:/vbscript: 스킴. DOMPurify 가 style 값 안의 위험 문법도 정화한다.
 */
export function sanitizeExternalMailHtml(rawHtml: string, opts?: SanitizeMailOptions): string {
  if (typeof rawHtml !== 'string' || rawHtml.length === 0) return '';
  const clean = DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: tagsFor([...ALLOWED_TAGS, 'font', 'center', 'tfoot', 'caption', 'col', 'colgroup', 'small', 'big'], opts),
    ALLOWED_ATTR: [...ALLOWED_ATTR, 'style', 'bgcolor', 'background', 'valign', 'nowrap', 'color', 'face', 'size', 'dir', 'lang'],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
    ADD_ATTR: ['target', 'rel'],
  });
  return clean.replace(/<a([^>]*?)target="_blank"((?:(?!rel=).)*?)>/gi, (match, before, after) => {
    if (/rel=/i.test(match)) return match;
    return `<a${before}target="_blank" rel="noopener noreferrer"${after}>`;
  });
}

/**
 * 채팅 AI 답변용 HTML sanitize — sanitizeMailHtml 과 동일 안전정책 + `style` 속성 허용.
 * 답변 포맷의 색상(팔레트·테두리)을 인라인 style 로 렌더하기 위함.
 * DOMPurify 가 style 값을 정화(javascript:/expression 등 위험 제거) — script/iframe/on* 은 그대로 차단.
 */
export function sanitizeChatHtml(rawHtml: string): string {
  if (typeof rawHtml !== 'string' || rawHtml.length === 0) return '';
  return DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: [...ALLOWED_ATTR, 'style'],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
  });
}

/**
 * 게시판 본문 HTML sanitize (2026-09-23 · 리치 에디터 Phase 1).
 *
 * 채팅과 같은 안전정책을 쓰되 **`style` 은 허용하지 않는다** — 게시판은 tiptap 스키마가
 * 이미 인라인 style 을 전부 버리므로(색·정렬·글꼴 확장이 없다) 허용해 봤자 통과할 것이
 * 없고, 허용 면적만 넓어진다.
 *
 * 대신 `data-file-id` 를 **살려 둔다.** 그것이 그림의 «오래 사는» 참조이고
 * (`components/editor/editor-html.ts`), 여기서 지워지면 저장할 때 주소를 다시 만들 수 없어
 * 그림이 통째로 사라진다.
 *
 * ⚠ 이것은 **두 겹 중 앞 겹**이다. 서버(chat-server)의 게시판 라우트는 아직 HTML sanitize 를
 *   **0회** 한다 — `board-post.routes.ts` 는 거부목록(`text-sanitize.ts`)만 부르고
 *   `board-notice.routes.ts` 는 그것조차 안 부른다(2026-09-23 실측). 뒤 겹은 다음 판에서
 *   `lib/html-sanitize.ts` 를 게시판에 붙여야 완성된다. 브라우저만 믿으면 안 된다.
 */
export function sanitizeBoardHtml(rawHtml: string): string {
  if (typeof rawHtml !== 'string' || rawHtml.length === 0) return '';
  return DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: [...ALLOWED_ATTR, 'data-file-id'],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
    ADD_ATTR: ['data-file-id'],
  });
}
